import { Portal, Stack } from '@lego/klik-ui';
import { useEffect, useMemo, useState } from 'react';
import { AnnouncementItem } from '@/components/announcements/AnnouncementItem';
import announcementsData from '../../constants/announcements.json';

export type Announcement = {
  header: string;
  summary: string;
  moreDetails: string;
  startDate: string;
  expDate: string;
  variant: 'plain' | 'info' | 'success' | 'warning' | 'error';
  id: number;
};

const DISMISSED_KEY = 'dismissedAnnouncements';

const announcements = announcementsData as Announcement[];

export const Announcements = () => {
  const [dismissedIds, setDismissedIds] = useState<number[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(DISMISSED_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setDismissedIds(parsed);
        }
      } catch (error) {
        console.error('Failed to parse dismissed announcements:', error);
      }
    }
  }, []);

  const handleDismiss = (id: number) => {
    const updated = [...dismissedIds, id];
    setDismissedIds(updated);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(updated));
  };

  const visibleAnnouncements = useMemo(() => {
    const now = new Date();

    return announcements.filter((announcement) => {
      const startDate = new Date(announcement.startDate);
      const expDate = new Date(announcement.expDate);

      const isWithinDateRange = now >= startDate && now <= expDate;
      const isNotDismissed = !dismissedIds.includes(announcement.id);

      return isWithinDateRange && isNotDismissed;
    });
  }, [dismissedIds]);

  if (visibleAnnouncements.length === 0) return null;

  return (
    <Portal>
      <Stack gap={4} left={0} mt={4} mx={6} position="absolute" right={0} top={70} zIndex={1400}>
        {visibleAnnouncements.map((announcement) => (
          <AnnouncementItem
            announcement={announcement}
            key={announcement.id}
            onDismiss={handleDismiss}
          />
        ))}
      </Stack>
    </Portal>
  );
};
